import { useEffect, type RefObject } from "react";
import { isTauri } from "@/lib/is-tauri";
import { isEditableTarget } from "@/lib/editable-target";

import { createShortcutKeyMatcher } from "@/lib/shortcuts";
import { useShortcutStore } from "@/stores/shortcut-store";

interface Point {
  x: number;
  y: number;
}

interface WorkspaceDoubleControlInputOptions {
  /** The workspace layer root; without a pointer the bar opens at its centre. */
  containerRef: RefObject<HTMLElement | null>;
  notePlacementActive: boolean;
  commentModeActive: boolean;
  /** A client point as a workspace point (the view adapter's). */
  toWorkspacePoint: (point: Point) => Point;
  openInput: (position: {
    flowX: number;
    flowY: number;
    placement: "bottom";
  }) => void;
  setInputValue: (value: string) => void;
}

/** Browser input summon uses the same saved binding; Tauri owns global dispatch. */
export function useWorkspaceDoubleControlInput({
  containerRef,
  notePlacementActive,
  commentModeActive,
  toWorkspacePoint,
  openInput,
  setInputValue,
}: WorkspaceDoubleControlInputOptions): void {
  useEffect(() => {
    if (isTauri()) return;
    void useShortcutStore.getState().hydrate();
    const el = containerRef.current;
    if (!el) return;

    let lastMouse: Point | null = null;
    let match = createShortcutKeyMatcher("shortcut.input.open");

    const onMouseMove = (event: MouseEvent) => {
      lastMouse = { x: event.clientX, y: event.clientY };
    };

    const onKey = (event: KeyboardEvent) => {
      if (
        notePlacementActive ||
        commentModeActive ||
        isEditableTarget(event.target)
      ) {
        match = createShortcutKeyMatcher("shortcut.input.open");
        return;
      }
      if (!match(event)) return;
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const point = lastMouse ?? {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      const flow = toWorkspacePoint(point);
      openInput({ flowX: flow.x, flowY: flow.y, placement: "bottom" });
      setInputValue("");
    };
    const resetSequence = () => {
      match = createShortcutKeyMatcher("shortcut.input.open");
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    window.addEventListener("blur", resetSequence);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("blur", resetSequence);
    };
  }, [
    commentModeActive,
    containerRef,
    notePlacementActive,
    openInput,
    setInputValue,
    toWorkspacePoint,
  ]);
}
